/** Monument registry — maps a monument key to its icon body, and renders
 * one in a chosen colourway. Tile art picks from DRAWN_MONUMENT_KEYS.
 */
import type { JSX } from "react";
import { COLORWAYS, MonumentFrame, type ColorwayKey } from "./frame";
import * as icons from "./icons";
import { MONUMENTS } from "./manifest";

const ICON_BODIES: Record<string, () => JSX.Element> = {
  "brandenburger-tor": icons.brandenburgerTor,
  "koelner-dom": icons.koelnerDom,
  "neuschwanstein": icons.neuschwanstein,
  "reichstag": icons.reichstag,
  "frauenkirche-dresden": icons.frauenkircheDresden,
  "eiffel-tower": icons.eiffelTower,
  "arc-de-triomphe": icons.arcDeTriomphe,
  "notre-dame": icons.notreDame,
  "sacre-coeur": icons.sacreCoeur,
  "mont-saint-michel": icons.montSaintMichel,
  "colosseum": icons.colosseum,
  "leaning-tower-pisa": icons.leaningTowerPisa,
  "st-peters": icons.stPeters,
  "st-marks": icons.stMarks,
  "trevi-fountain": icons.treviFountain,
  "duomo-milano": icons.duomoMilano,
  "big-ben": icons.bigBen,
  "tower-bridge": icons.towerBridge,
  "london-eye": icons.londonEye,
  "stonehenge": icons.stonehenge,
  "edinburgh-castle": icons.edinburghCastle,
  "cliffs-of-moher": icons.cliffsOfMoher,
  "sagrada-familia": icons.sagradaFamilia,
  "alhambra": icons.alhambra,
  "guggenheim-bilbao": icons.guggenheimBilbao,
  "belem-tower": icons.belemTower,
  "clerigos-tower": icons.clerigosTower,
  "atomium": icons.atomium,
  "grand-place": icons.grandPlace,
  "windmills-kinderdijk": icons.windmillsKinderdijk,
  "rijksmuseum": icons.rijksmuseum,
  "charles-bridge": icons.charlesBridge,
  "prague-castle": icons.pragueCastle,
  "astronomical-clock": icons.astronomicalClock,
  "schonbrunn": icons.schonbrunn,
  "stephansdom": icons.stephansdom,
  "chapel-bridge": icons.chapelBridge,
  "matterhorn": icons.matterhorn,
  "wawel-castle": icons.wawelCastle,
  "warsaw-palace": icons.warsawPalace,
  "hungarian-parliament": icons.hungarianParliament,
  "fishermans-bastion": icons.fishermansBastion,
  "parthenon": icons.parthenon,
  "meteora": icons.meteora,
  "santorini": icons.santorini,
  "hagia-sophia": icons.hagiaSophia,
  "little-mermaid": icons.littleMermaid,
  "nyhavn": icons.nyhavn,
  "st-basils": icons.stBasils,
  "hermitage": icons.hermitage,
};

/** Keys with a drawn vector, in manifest order — the set tile art draws
 * from. Derived from the manifest AND guarded by the registry, so a
 * `drawn: true` without a registered body never leaks to the tiles. */
export const DRAWN_MONUMENT_KEYS: string[] = MONUMENTS.filter(
  (m) => m.drawn && ICON_BODIES[m.key],
).map((m) => m.key);

const NAME_BY_KEY: Record<string, string> = Object.fromEntries(
  MONUMENTS.map((m) => [m.key, m.name]),
);

export function MonumentIcon({
  monumentKey,
  colorway = "sandGold",
  className = "",
}: {
  monumentKey: string;
  colorway?: ColorwayKey;
  className?: string;
}) {
  const body = ICON_BODIES[monumentKey];
  if (!body) return null;
  return (
    <MonumentFrame
      colors={COLORWAYS[colorway]}
      title={NAME_BY_KEY[monumentKey] ?? monumentKey}
      className={className}
    >
      {body()}
    </MonumentFrame>
  );
}
